import {
  reduce,
  isEmpty,
  keyBy,
  isNil,
  get
} from 'lodash';
import { createSelector } from 'reselect';
import {
  ELSTokenHelper,
  ELSCommonConfig
} from '@els/els-ui-common-react';
import {
  AdobeAnalyticsPageData,
  AssignmentManagementStore
} from './ama.models';
import { reduxStateDomains } from '../redux.constants';
import {
  filterNotFoundError,
  includesConflictError,
  getEvolveResourcesFromCourseSection,
  getNormalizedInstitution
} from '../redux.utilities';
import {
  FALSE_VALUE,
  FEATURE_FLAG,
  TRUE_VALUE
} from '../../apis/eols-features-api/eols-features-api.constants';
import {
  AssessmentDto,
  AssessmentStatusDto,
  AssignmentDto,
  AssignmentQuestionDto
} from '../../apis/eols-assessment-service/eols-assessment-service.dtos';
import {
  ExternalEntityDto,
  OsmosisTokenDto
} from '../../apis/sherpath-course-management-service/sherpath-course-management-service.dtos';
import { AppLinkCookies, AppLinkData } from '../../apis/eols-app-link/eols-app-link.dtos';
import {
  ActiveSyllabusItemTypeDto,
  ExternalIdTypeDto
} from '../../apis/sherpath-syllabus-service/sherpath-syllabus-service.constants';
import { ELSCourseSectionDto } from '../../models/els.dtos';
import { getAssociatedAssignment } from '../../pages/course-plan/syllabus.utilities';
import { ResourceStatusMap } from '../../pages/catalog/catalog.models';
import { getSortedSyllabusTreeMapItems } from '../../pages/course-builder/courseBuilder.utilities';
import { CrosswalkUserDto } from '../../apis/eols-user-management-service/eols-user-management-service.dtos';
import {
  getContentItemIdFromAssignment,
  getSortedSyllabusFolderOrderMap,
} from '../../pages/catalog/catalog.utilities';
import {
  getABPropsForAnalytics,
  getBooleanFromGroupFeatureFlagWithFallbackToGlobal,
} from '../../utilities/featureFlag.utilities';
import {
  SequenceMap,
  SkillQuestionStaticData,
  SkillSubmissionData
} from '../../apis/ocs-api-service/ocs-api-service.dtos';
import { SyllabusItemDto } from '../../apis/sherpath-syllabus-service/sherpath-syllabus-service.dtos';
import { scrubProps } from '../../utilities/analytics.utilities';
import { locationSelectors } from '../location/location.selectors';
import { EolsUserEngagementDto } from '../../apis/eols-user-engagement/eols-user-engagement.dtos';
import { UNKNOWN } from './ama.constants';
import {
  getEmptyPageData,
  getSemester
} from '../../utilities/adobe-analytics-utilities';
import {
  isInstructor,
  isStudent
} from '../../utilities/common.utilities';
import { convertIsbnsToNormalizedString } from '../../utilities/app.utilities';
import { MigratedCourseEntitlementDto } from '../../apis/sherpath-course-management-service/sherpath-course-management-service.utilities';
import { FeatureFlagDto } from '../../apis/eols-features-api/eols-features-api.dtos';
import { Application } from '../../apis/eols-app-link/eols-app-link.constants';
import {
  RecContentItemDto
} from '../../apis/rec-gateway/rec-gateway.dtos';

const getAmaStore = (globalState): AssignmentManagementStore => {
  return globalState[reduxStateDomains.AMA_STATE];
};

const getCourseSectionId = globalState => getAmaStore(globalState).courseSectionId;

const getRoleId = globalState => getAmaStore(globalState).roleId;

const getUserId = globalState => getAmaStore(globalState).userId;

const getRegisteredToken = globalState => getAmaStore(globalState).registeredToken;

const getIsLoading = globalState => {
  return getAmaStore(globalState).pendingRequestCount > 0;
};

const getCourseName = globalState => {
  const appStore = getAmaStore(globalState);
  return get(appStore, 'currentCourse.courseName', '');
};

const getIsCourseLocked = globalState => {
  const appStore = getAmaStore(globalState);
  return get(appStore, 'currentCourse.locked', false);
};

const getCourse = globalState => getAmaStore(globalState).currentCourse;

const getUsers = globalState => getAmaStore(globalState).users;

const getEaqUsers = globalState => getAmaStore(globalState).eaqUsers;

const getEaqIsbns = globalState => getAmaStore(globalState).eaqIsbns;

const getMasteryResetSuccess = globalState => getAmaStore(globalState).masteryResetSuccess;

const getRawIsbns = globalState => getAmaStore(globalState).isbns;
const getIsbns = createSelector(
  getRawIsbns,
  (isbns) => {
    if (!isbns) {
      return null;
    }
    return isbns.split(',');
  }
);

const getVantageIsbns = (globalState) => {
  return getAmaStore(globalState).vantageIsbns;
};

const getEvolveProducts = createSelector(
  [getIsbns, getVantageIsbns, getCourse],
  getEvolveResourcesFromCourseSection
);

const getUser = globalState => {
  const {
    userId,
    users
  } = getAmaStore(globalState);

  if (!users) {
    return null;
  }

  return users.find(x => x.id === parseInt(userId, 10));
};

const getUserDisplayName = createSelector(
  getUser,
  (user) => {
    if (!user) {
      return null;
    }
    return `${user.firstName}`;
  }
);

const getUserHistory = globalState => {
  return getAmaStore(globalState).userHistory;
};

const getUserHistoryStateCompletedRequests = globalState => {
  return getAmaStore(globalState).userHistoryStateCompletedRequests;
};

const getCurrentUserCourseOwnerRecords = globalState => {
  return getAmaStore(globalState).userCourseOwnerRecords;
};

const getSyllabusFolderInfo = (globalState) => {
  return getAmaStore(globalState).syllabusFolderInfo;
};

const getCurrentUserCourseOwnerRecord = createSelector(
  getCurrentUserCourseOwnerRecords,
  (userCourseOwnerRecords) => {
    if (!userCourseOwnerRecords || !userCourseOwnerRecords.length) {
      return null;
    }

    return userCourseOwnerRecords[0];
  }
);

const getIsCourseOwner = createSelector(
  getCurrentUserCourseOwnerRecord,
  (matchingUserRecord) => {
    if (!matchingUserRecord) {
      return false;
    }

    return matchingUserRecord.stateInfo === TRUE_VALUE;
  }
);

const getErrors = globalState => {
  const amaStore = globalState[reduxStateDomains.AMA_STATE] as AssignmentManagementStore;

  return amaStore.errors;
};

const getToastableErrors = globalState => {
  const errors = getErrors(globalState);
  return filterNotFoundError(errors);
};

const getConflictErrors = globalState => {
  const errors = getErrors(globalState);
  return includesConflictError(errors);
};

const getInstitution = globalState => {
  return getAmaStore(globalState).currentCourse && getAmaStore(globalState).currentCourse.institution;
};

const getMessages = globalState => getAmaStore(globalState).messages;

const getIsLoadingLanguage = globalState => isEmpty(getAmaStore(globalState).messages);

const getFeatureFlagsGrouped = globalState => getAmaStore(globalState).featureFlagsGrouped;

const getIsMaintenance = createSelector(
  [getFeatureFlagsGrouped, getCourseSectionId],
  (flags, courseSectionId) => {
    const value = getBooleanFromGroupFeatureFlagWithFallbackToGlobal(flags, FEATURE_FLAG.IS_SITE_DOWN_FOR_MAINTENANCE, courseSectionId);
    return value === null ? false : value;
  }
);

const getCatalog = (globalState) => getAmaStore(globalState).catalog;

const getPrimaryTaxonomies = (globalState) => getAmaStore(globalState).primaryTaxonomies;

const getTaxonomies = (globalState) => getAmaStore(globalState).taxonomies;

const getAllSyllabusItems = globalState => getAmaStore(globalState).syllabusItems;
const getSyllabusItems = createSelector(
  getAllSyllabusItems,
  (syllabusItems) => {
    return syllabusItems.filter(item => !item.isDeleted);
  }
);
const getAssignments = (globalState): AssignmentDto[] => getAmaStore(globalState).assignments;
const getAssignmentsDictionary = createSelector(
  getAssignments,
  (assignments) => {
    return keyBy(assignments, 'id');
  }
);
const getExternalEntities = (globalState): ExternalEntityDto[] => getAmaStore(globalState).externalEntities;
const getSyllabusFolders = globalState => {
  return getAmaStore(globalState).syllabusItems.filter(item => item.type === ActiveSyllabusItemTypeDto.FOLDER);
};

const getIsBatchEditModeEnabled = (globalState) => {
  return getAmaStore(globalState).isBatchEditModeEnabled;
};

const getIsDragDropModeEnabled = (globalState) => {
  return getIsCourseLocked(globalState) ? false : getAmaStore(globalState).isDragDropModeEnabled;
};

const getCheckedSyllabusItemIds = (globalState) => {
  return getAmaStore(globalState).checkedSyllabusItemIds;
};

const getCourseBuilderState = (globalState) => {
  return getAmaStore(globalState).courseBuilderState;
};

const getAssessmentSubmissionsMap = (globalState) => {
  return getAmaStore(globalState).assessmentSubmissionsMap;
};

const getGroupActivity = (globalState) => {
  return getAmaStore(globalState).groupActivity;
};

const getEolsUserCourseSections = (globalState): ELSCourseSectionDto[] => {
  return get(getAmaStore(globalState), 'eolsUser.courseSections', []);
};

const getEolsCrosswalkUserCourseSections = (globalState): ELSCourseSectionDto[] => {
  const { crosswalkUsers = [] } = getAmaStore(globalState);
  return crosswalkUsers.map(user => user.courseSections).flat();
};

const getResourceStatusMap = createSelector(
  [getSyllabusItems, getAssignments, getIsLoading],
  // eslint-disable-next-line sonarjs/cognitive-complexity
  (syllabusItems, assignments, isLoading) => {
    if (isLoading || syllabusItems.length === 0) {
      return {};
    }

    const sortedSyllabusTreeMapItems = getSortedSyllabusTreeMapItems(syllabusItems);
    const sortedSyllabusFolderMap = getSortedSyllabusFolderOrderMap(sortedSyllabusTreeMapItems);
    const resourceStatusMap = syllabusItems.reduce<ResourceStatusMap>((acc, cur) => {
      const { parentId, type } = cur;
      const catalogExId = cur.externalIdentifiers.find(exId => exId.type === ExternalIdTypeDto.CATALOG_ITEM_ID);
      const assignmentId = cur.externalIdentifiers.find(exId => exId.type === ExternalIdTypeDto.ASSIGNMENT_ID);
      if (!parentId || type === ActiveSyllabusItemTypeDto.EBOOK_READING || (!catalogExId && !assignmentId)) {
        return acc;
      }
      const parentItem = syllabusItems.find(item => item.id === parentId);
      const associatedAssignment = getAssociatedAssignment(cur, assignments);
      const isVisible = associatedAssignment && !!associatedAssignment.availableDate;
      const isAssigned = isVisible && !!associatedAssignment.dueDate;

      if (!associatedAssignment && !catalogExId) {
        return acc;
      }

      const contentItemId = associatedAssignment ? getContentItemIdFromAssignment(associatedAssignment) : catalogExId.value;

      let status = 'added';
      if (isAssigned) {
        status = 'assigned';
      } else if (isVisible) {
        status = 'visible';
      }
      if (acc[contentItemId]) {
        return {
          ...acc,
          [contentItemId]: {
            ...acc[contentItemId],
            [status]: acc[contentItemId][status].concat([{
              syllabusItem: cur,
              parentItem
            }]),
          }
        };
      }
      return {
        ...acc,
        [contentItemId]: {
          visible: [],
          assigned: [],
          added: [],
          [status]: [{
            syllabusItem: cur,
            parentItem,
          }],
        },
      };
    }, {});

    const sortFn = (a, b) => {
      if (!a.parentItem || !b.parentItem) {
        return 0;
      }
      if (!sortedSyllabusFolderMap[a.parentItem.id] || !sortedSyllabusFolderMap[b.parentItem.id]) {
        return 0;
      }
      return sortedSyllabusFolderMap[a.parentItem.id].order - sortedSyllabusFolderMap[b.parentItem.id].order;
    };

    return reduce(resourceStatusMap, (acc, mapItem, key) => {
      return {
        ...acc,
        [key]: {
          added: mapItem.added.sort(sortFn),
          visible: mapItem.visible.sort(sortFn),
          assigned: mapItem.assigned.sort(sortFn),
        }
      };
    }, {});
  }
);

const getEvolveUser = (globalState): CrosswalkUserDto => {
  return getAmaStore(globalState).evolveUser;
};

const getAssessmentStartTimeMap = (globalState): Record<string, Date> => {
  return getAmaStore(globalState).assessmentStartTimeMap;
};

const getAppLinkCookies = (globalState): AppLinkCookies => {
  return getAmaStore(globalState).appLinkCookies;
};

const getLinkData = (globalState): AppLinkData => {
  return getAmaStore(globalState).linkData;
};

const getCollapsedFolderIds = (globalState): string[] => {
  return getAmaStore(globalState).collapsedFolderIds;
};

const getSkillSubmissionData = (globalState): {
  submissionData: SkillSubmissionData[];
  vtwId: string;
  skillStaticData: SkillQuestionStaticData[];
  skillVersionVtwId: string;
  stateKey: string;
} => {
  const { skillSubmissionRecords, skillStaticData } = getAmaStore(globalState);
  if (isNil(skillSubmissionRecords) || isNil(skillStaticData)) {
    return null;
  }
  const parsedStateInfo = JSON.parse(skillSubmissionRecords[0].stateInfo);
  return {
    submissionData: parsedStateInfo.submissionData,
    vtwId: skillStaticData.vtwId,
    skillVersionVtwId: parsedStateInfo.skillVersionVtwId,
    skillStaticData: JSON.parse(skillStaticData.data),
    stateKey: skillSubmissionRecords[0].stateKey,
  };
};

const getBatchEditSelectedSyllabusItems = (globalState): SyllabusItemDto[] => {
  return getAmaStore(globalState).batchEditSelectedSyllabusItems;
};

const getBatchEditUpdatedSyllabusItems = (globalState): SyllabusItemDto[] => {
  return getAmaStore(globalState).batchEditUpdatedSyllabusItems;
};

const getABTestFlavors = (globalState): FeatureFlagDto[] => {
  return getAmaStore(globalState).abTestFlavors;
};

const getUserEmailDomain = (globalState): string => {
  const user = getUser(globalState);
  const emailAddress = !user || !user.emailAddress ? ELSTokenHelper.getUserEmail() : user.emailAddress;
  if (!emailAddress) {
    return UNKNOWN;
  }
  const emailParts = emailAddress.split('@');
  if (!emailParts || emailParts.length !== 2) {
    return UNKNOWN;
  }
  return emailParts[1];
};

const isTestUser = (globalState): string => {
  const emailDomain = getUserEmailDomain(globalState);
  if (emailDomain === UNKNOWN) {
    return UNKNOWN;
  }

  const _isTestUser = ELSCommonConfig.testUserEmailDomains.some((domain) => {
    return emailDomain.includes(domain);
  });
  return _isTestUser ? TRUE_VALUE : FALSE_VALUE;
};

const getDefaultActionProps = (globalState): Record<string, string | number | string[] | number[]> => {
  const evolveUser = getEvolveUser(globalState);
  const institution = getInstitution(globalState);
  const abTestFlavors = getABTestFlavors(globalState);
  const props = {
    courseSectionId: getCourseSectionId(globalState),
    eolsUserId: getUserId(globalState),
    evolveUserId: evolveUser ? evolveUser.externalUserId : UNKNOWN,
    userRole: getRoleId(globalState),
    isbns: getRawIsbns(globalState),
    isbnsSorted: convertIsbnsToNormalizedString(getIsbns(globalState)),
    institution: institution ? institution.name : null,
    userEmailDomain: getUserEmailDomain(globalState),
    isTestUser: isTestUser(globalState),
    isMasqueradeUser: ELSTokenHelper.isMasqueradeUser(),
    pathname: locationSelectors.getLocation(globalState).pathname,
    institutionNormalized: getNormalizedInstitution(getUserEmailDomain(globalState)),
    applicationId: Application.ASSIGNMENT_MANAGEMENT,
    ...getABPropsForAnalytics(abTestFlavors)
  };
  return scrubProps(props);
};

const getDefaultAdobeAnalyticsPageData = (globalState): AdobeAnalyticsPageData => {

  const pageData: AdobeAnalyticsPageData = window && window.pageData
    ? window.pageData as AdobeAnalyticsPageData
    : getEmptyPageData();

  return {
    ...pageData,
    education: {
      ...pageData.education,
      courseId: getCourseSectionId(globalState),
      courseName: getCourseName(globalState),
      isbn: convertIsbnsToNormalizedString(getIsbns(globalState)),
      studentId: isStudent(getRoleId(globalState)) ? pageData.visitor.userId || getUserId(globalState) : '',
      instructorId: isInstructor(getRoleId(globalState)) ? pageData.visitor.userId || getUserId(globalState) : '',
      semester: getSemester(),
    }
  };
};

const getUserEngagementReport = (globalState): EolsUserEngagementDto[] => {
  return getAmaStore(globalState).userEngagementReport;
};

const getModuleSequenceMap = (globalState): SequenceMap => {
  return getAmaStore(globalState).moduleSequenceMap;
};

const getAssessments = (globalState): AssessmentDto[] => {
  return getAmaStore(globalState).assessments;
};

const getEnableDeepLink = (globalState): boolean => {
  return getAmaStore(globalState).enableDeepLink;
};

const getAssignmentInProgressAssessmentMap = createSelector(
  [getAssessments],
  (assessments): Record<number, number> => {
    return assessments.reduce((assignmentInProgressAssessmentMap, curAssessment): Record<number, number> => {
      if (curAssessment.status === AssessmentStatusDto.IN_PROGRESS) {
        return {
          ...assignmentInProgressAssessmentMap,
          [curAssessment.assignmentId]: curAssessment.id,
        };
      }
      return assignmentInProgressAssessmentMap;
    }, {});
  }
);

const getHasRunAuthessHealthCheck = (globalState): boolean => {
  return getAmaStore(globalState).hasRunAuthessHealthCheck;
};

const getOsmosisTokenDto = (globalState): OsmosisTokenDto => {
  return getAmaStore(globalState).osmosisTokenDto;
};

const getMigratedEntitlements = (globalState): MigratedCourseEntitlementDto[] => {
  return getAmaStore(globalState).migratedEntitlements;
};

const getSLSQuizQuestions = (globalState): AssignmentQuestionDto[] => {
  return getAmaStore(globalState).slsQuizQuestions;
};

const getAssignment = (globalState): AssignmentDto => {
  return getAmaStore(globalState).assignment;
};

const getInteractiveReviewCatalogItem = (globalState): RecContentItemDto => {
  return getAmaStore(globalState).interactiveReviewCatalogItem;
};

const getImportSelection = (globalState): Partial<AssignmentDto> => {
  return getAmaStore(globalState).importSelection;
};

const getAssignmentEditorCatalogItem = (globalState): RecContentItemDto => {
  return getAmaStore(globalState).assignmentEditorCatalogItem;
};

const getAssignmentEditorFoundAssignment = (globalState): Partial<AssignmentDto> => {
  return getAmaStore(globalState).assignmentEditorFoundAssignment;
};

const getLessonTopics = (globalState) => {
  return getAmaStore(globalState).lessonTopics;
};

export const amaSelectors = {
  getAmaStore,
  getCourse,
  getCourseName,
  getIsCourseLocked,
  getUserHistory,
  getUserHistoryStateCompletedRequests,
  getCourseSectionId,
  getCurrentUserCourseOwnerRecord,
  getCurrentUserCourseOwnerRecords,
  getErrors,
  getEvolveProducts,
  getInstitution,
  getIsbns,
  getVantageIsbns,
  getIsCourseOwner,
  getIsLoading,
  getIsLoadingLanguage,
  getMessages,
  getRoleId,
  getToastableErrors,
  getUser,
  getUserDisplayName,
  getUserId,
  getRegisteredToken,
  getUsers,
  getEaqUsers,
  getEaqIsbns,
  getMasteryResetSuccess,
  getCatalog,
  getPrimaryTaxonomies,
  getTaxonomies,
  getAllSyllabusItems,
  getSyllabusItems,
  getAssignments,
  getAssignmentsDictionary,
  getExternalEntities,
  getSyllabusFolders,
  getIsBatchEditModeEnabled,
  getIsDragDropModeEnabled,
  getCheckedSyllabusItemIds,
  getCourseBuilderState,
  getAssessmentSubmissionsMap,
  getEolsUserCourseSections,
  getEolsCrosswalkUserCourseSections,
  getResourceStatusMap,
  getEvolveUser,
  getAssessmentStartTimeMap,
  getFeatureFlagsGrouped,
  getAppLinkCookies,
  getLinkData,
  getCollapsedFolderIds,
  getSkillSubmissionData,
  getBatchEditSelectedSyllabusItems,
  getBatchEditUpdatedSyllabusItems,
  getDefaultActionProps,
  getIsMaintenance,
  isTestUser,
  getUserEngagementReport,
  getModuleSequenceMap,
  getAssessments,
  getAssignmentInProgressAssessmentMap,
  getGroupActivity,
  getDefaultAdobeAnalyticsPageData,
  getEnableDeepLink,
  getHasRunAuthessHealthCheck,
  getConflictErrors,
  getOsmosisTokenDto,
  getMigratedEntitlements,
  getSyllabusFolderInfo,
  getABTestFlavors,
  getAssignment,
  getInteractiveReviewCatalogItem,
  getImportSelection,
  getAssignmentEditorCatalogItem,
  getAssignmentEditorFoundAssignment,
  getSLSQuizQuestions,
  getLessonTopics,
};
